import React, { useState, useEffect } from 'react';

const PLACEHOLDER = 'https://placehold.co/400x220/e8f5e9/198754?text=No+Image';

export default function CoverFlow({ grouped = {}, onServiceClick }) {
  const entries = Object.entries(grouped);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (entries.length === 0) return;
    const timer = setInterval(() => {
      setActive(prev => (prev + 1) % entries.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [entries.length]);

  if (entries.length === 0) return null;

  const getClass = (index) => {
    const len = entries.length;
    let diff = index - active;
    if (diff > len / 2) diff -= len;
    if (diff < -len / 2) diff += len;
    if (diff === 0) return 'active';
    if (diff === -1) return 'left1';
    if (diff === 1) return 'right1';
    if (diff === -2) return 'left2';
    if (diff === 2) return 'right2';
    return 'hidden';
  };

  return (
    <div className="coverflow-wrapper mb-5">
      <div className="coverflow-container">
        <button
          className="nav prev"
          onClick={() => setActive(a => (a - 1 + entries.length) % entries.length)}
        >‹</button>

        <div className="coverflow">
          {entries.map(([tradeName, group], i) => {
            // find a real cover image from the group
            const coverCraftsman = group.find(c =>
              c.gallery_images?.[0]?.image_url ||
              c.services?.[0]?.image_url ||
              c.services?.[0]?.image ||
              c.service_image
            );
            const src = coverCraftsman
              ? (coverCraftsman.gallery_images?.[0]?.image_url ||
                 coverCraftsman.services?.[0]?.image_url ||
                 coverCraftsman.services?.[0]?.image ||
                 coverCraftsman.service_image)
              : null;
            const locs = [...new Set(group.map(c => c.location).filter(Boolean))];
            const locLabel = locs.slice(0, 2).join(', ') + (locs.length > 2 ? ` +${locs.length - 2} more` : '');
            const count = group.length;

            return (
              <div
                key={tradeName}
                className={`coverflow-item ${getClass(i)}`}
                onClick={() => getClass(i) === 'active' && onServiceClick && onServiceClick(tradeName, src)}
                style={{ cursor: getClass(i) === 'active' ? 'pointer' : 'default' }}
              >
                {/* Cover image */}
                <img
                  src={src || PLACEHOLDER}
                  alt={tradeName}
                  onError={e => { e.target.src = PLACEHOLDER; }}
                />

                {/* Overlay info */}
                <div className="cf-overlay">
                  <div className="cf-badge-count">
                    {count} {count === 1 ? 'craftsman' : 'craftsmen'}
                  </div>
                  <h3 className="cf-trade-name">{tradeName}</h3>
                  {locLabel && (
                    <p className="cf-location">
                      <i className="fas fa-map-marker-alt" style={{marginRight:4}}/>{locLabel}
                    </p>
                  )}
                  <div className="cf-cta">
                    <i className="fas fa-users" style={{marginRight:6}}/>
                    View {tradeName} Craftsmen
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          className="nav next"
          onClick={() => setActive(a => (a + 1) % entries.length)}
        >›</button>
      </div>

      {/* Dots */}
      <div className="dots">
        {entries.map((_, i) => (
          <span
            key={i}
            className={`dot ${i === active ? 'active' : ''}`}
            onClick={() => setActive(i)}
          />
        ))}
      </div>

      <style>{`
        .coverflow-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }
        .coverflow-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: 1200px;
          position: relative;
        }
        .coverflow {
          perspective: 1400px;
          position: relative;
          width: 100%;
          height: 340px;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: visible;
        }
        .coverflow-item {
          position: absolute;
          width: 420px;
          height: 270px;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          transition: all 0.6s cubic-bezier(0.4,0,0.2,1);
          opacity: 0;
          pointer-events: none;
        }
        .coverflow-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* Overlay */
        .cf-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.72) 100%);
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 18px 20px;
          gap: 5px;
        }
        .cf-badge-count {
          display: inline-flex;
          align-self: flex-start;
          background: rgba(255,215,0,0.93);
          color: #1a1a2e;
          border-radius: 20px;
          padding: 2px 12px;
          font-size: .7rem;
          font-weight: 800;
          margin-bottom: 2px;
        }
        .cf-trade-name {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.1rem, 2.5vw, 1.5rem);
          font-weight: 800;
          color: #fff;
          margin: 0;
          line-height: 1.2;
        }
        .cf-location {
          font-size: .75rem;
          color: rgba(255,255,255,.82);
          margin: 0;
          font-weight: 500;
        }
        .cf-cta {
          background: #198754;
          color: #fff;
          border-radius: 8px;
          padding: 7px 14px;
          font-size: .78rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          margin-top: 4px;
          align-self: flex-start;
          transition: background .18s;
        }
        .coverflow-item.active .cf-cta:hover { background: #145a32; }

        /* Position classes */
        .active {
          transform: translateX(0) scale(1);
          z-index: 5;
          opacity: 1;
          pointer-events: auto;
        }
        .left1 {
          transform: translateX(-240px) scale(0.85) rotateY(35deg);
          z-index: 4;
          opacity: 0.8;
          pointer-events: auto;
        }
        .right1 {
          transform: translateX(240px) scale(0.85) rotateY(-35deg);
          z-index: 4;
          opacity: 0.8;
          pointer-events: auto;
        }
        .left2 {
          transform: translateX(-400px) scale(0.7) rotateY(45deg);
          z-index: 3;
          opacity: 0.4;
        }
        .right2 {
          transform: translateX(400px) scale(0.7) rotateY(-45deg);
          z-index: 3;
          opacity: 0.4;
        }
        .hidden { opacity: 0; z-index: 0; }

        .nav {
          background: none;
          border: none;
          color: #198754;
          font-size: 4rem;
          cursor: pointer;
          z-index: 10;
          padding: 0 20px;
          transition: color 0.3s;
          line-height: 1;
          flex-shrink: 0;
        }
        .nav:hover { color: #146c43; }

        .dots { margin-top: 20px; display: flex; gap: 8px; }
        .dot {
          width: 10px; height: 10px; border-radius: 50%;
          background: #ccc; cursor: pointer; transition: background 0.3s;
        }
        .dot.active { background: #198754; }

        @media (max-width: 992px) {
          .coverflow-item { width: 320px; height: 210px; }
          .left1 { transform: translateX(-180px) scale(0.85) rotateY(35deg); }
          .right1 { transform: translateX(180px) scale(0.85) rotateY(-35deg); }
          .left2, .right2 { opacity: 0; }
        }
        @media (max-width: 768px) {
          .nav { display: none; }
          .coverflow-item { width: 280px; height: 185px; }
          .left1 { transform: translateX(-100px) scale(0.8); }
          .right1 { transform: translateX(100px) scale(0.8); }
        }
      `}</style>
    </div>
  );
}